import { useEffect, useState } from "react";
import { getCatalogIncident, isAuthenticationError } from "./api";
import type { CatalogIncident } from "./types";

export function CatalogIncidentDetails({ jobId, onSessionExpired }: { jobId: string; onSessionExpired: () => void }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<CatalogIncident | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    let current = true;
    setLoading(true); setError(false); setData(null);
    void getCatalogIncident(jobId).then(value => {
      if (value.schema !== "abora-catalog-incident-v1" || value.job_id !== jobId) throw Error("Invalid incident");
      if (current) setData(value);
    }).catch(cause => {
      if (isAuthenticationError(cause)) onSessionExpired();
      else if (current) setError(true);
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [jobId, open, onSessionExpired]);
  const count = (value: number | null) => value === null ? "Sin dato" : value.toLocaleString("es-ES");
  return <section className="catalog-incident" aria-label="Detalle de la incidencia">
    <button type="button" className="secondary-button" aria-expanded={open} onClick={() => setOpen(!open)}>
      {open ? "Cerrar detalle" : "Ver detalle de la incidencia"}
    </button>
    {open && <div className="catalog-incident-body">
      {loading && <p role="status">Consultando recibos del ciclo…</p>}
      {error && <p role="alert">No se pudo obtener el detalle. El ciclo permanece sin cambios; cierra y vuelve a abrir para consultar de nuevo.</p>}
      {data && <>
        <h3>{data.title}</h3><code>{data.error_code}</code>
        <p>{data.explanation}</p>
        <dl className="mini-kv">
          <div><dt>Ciclo</dt><dd><code>{data.cycle_id || "Sin ciclo relacionado"}</code></dd></div>
          <div><dt>Lote</dt><dd>{count(data.batch_number)} <code>{data.batch_job_id || "Sin lote identificado"}</code></dd></div>
          <div><dt>Lotes completos</dt><dd>{count(data.completed_batches)} de {count(data.total_batches)}</dd></div>
          <div><dt>Filas aplicadas en este lote</dt><dd>{count(data.rows_applied)}</dd></div>
          <div><dt>Productos actualizados / creados</dt><dd>{count(data.products_updated)} / {count(data.products_created)}</dd></div>
          <div><dt>Diferencias en conciliación</dt><dd>{count(data.reconciliation_mismatches)}</dd></div>
          <div><dt>Productos protegidos en la captura</dt><dd>{count(data.protected_snapshot_count)}</dd></div>
        </dl>
        <p><strong>Pasos confirmados: </strong>{data.confirmed_steps.map(step => step.label).join(" → ") || "No hay recibos confirmados"}.</p>
        {data.next_unconfirmed_step && <p><strong>Siguiente paso sin confirmar: </strong>{data.next_unconfirmed_step.label}. La ausencia de recibo no prueba que no haya escrituras parciales.</p>}
        {data.native_apply_confirmed && <p className="snapshot-alert warning">La aplicación de productos ya tiene recibo correcto. El lote completo sigue fallido y no se ha revertido automáticamente.</p>}
        <p>{data.evidence_limit}</p><p><strong>{data.recovery_advice}</strong></p>
      </>}
    </div>}
  </section>;
}
